"use client";

import {
  BillingBanner,
  getBillingBannerState,
  type BillingBannerActionKind,
} from "@focal/ui-kit";
import { useBillingState } from "@/hooks/useBillingState";
import { useSessionContext } from "@/context";
import { isUserAdmin } from "@/lib/user-utils";
import { routes } from "@/constants";
import { Role } from "@/types";
import { canNavigateAway } from "@/lib/navigationGuard";

interface BillingStatusBannerProps {
  className?: string;
}

// contact-support is handled inside BillingBanner (mailto) and never
// dispatched here; every other action leads to the billing portal
const handleAction = (kind: BillingBannerActionKind) => {
  switch (kind) {
    case "add-payment-method":
    case "update-payment":
    case "upgrade":
    case "manage-seats":
      if (!canNavigateAway(routes.admin.billing)) return;
      // Full page load — the admin portal is hosted separately from the app
      window.location.href = routes.admin.billing;
      return;
  }
};

export const BillingStatusBanner = ({
  className,
}: BillingStatusBannerProps) => {
  const { billingState, isLoading, isError } = useBillingState();
  const { session } = useSessionContext();

  const audience = isUserAdmin(
    session?.roles?.map((role) => role.role_id as Role),
  )
    ? "admin"
    : "member";

  // Network errors must not trigger false-positive subscription warnings
  if (isLoading || isError || !billingState) {
    return null;
  }

  // One timestamp for both evaluations so the pre-check and BillingBanner
  // can never disagree at a deadline boundary
  const now = Date.now();

  // Skip the padded wrapper entirely when there is nothing to show
  if (!getBillingBannerState(billingState, { audience, now })) {
    return null;
  }

  return (
    <div
      className="relative z-10 px-2 py-1"
      data-cy="subscription-status-banner"
    >
      <BillingBanner
        state={billingState}
        audience={audience}
        now={now}
        onAction={handleAction}
        className={className}
        data-cy="subscription-banner"
      />
    </div>
  );
};
